<!-- Enhanced search results UI with improved UX -->
<div v-if="show" class="search-overlay d-none">
  <!-- Overlay with backdrop blur -->
  <div
    class="search-backdrop"
    @click="$emit('close')"
  ></div>

  <!-- Card -->
  <div class="search-card bg-white shadow-lg rounded-3 overflow-hidden d-none">
    <!-- Header - Fixed -->
    <div class="search-header bg-light border-bottom px-4 py-3 d-flex justify-content-between align-items-center">
      <div class="flex-grow-1">
        <h6 class="mb-0 fw-semibold">Search Results</h6>
        <p class="search-header-subtitle text-muted mb-0">
          <span v-if="!is_searching && results.length">{{ results.length }} result{{ results.length !== 1 ? 's' : '' }} for "<span class="text-dark">{{ searchQuery }}</span>"</span>
          <span v-else-if="is_searching">Searching for "<span class="text-dark">{{ searchQuery }}</span>"</span>
        </p>
      </div>
      <button
        type="button"
        class="btn-close"
        aria-label="Close"
        @click="$emit('close')"
      ></button>
    </div>

    <!-- Scrollable Content -->
    <div class="search-content overflow-auto">
      <!-- Loading -->
      <div
        v-if="is_searching"
        class="search-loading d-flex flex-column align-items-center justify-content-center text-secondary py-5"
      >
        <div class="spinner-border text-primary mb-3" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <span class="search-loading-text text-muted">Searching...</span>
      </div>

      <!-- Error -->
      <div v-else-if="error_message" class="p-4">
        <div class="alert alert-danger mb-0 d-flex align-items-start">
          <i class="bi bi-exclamation-circle search-error-icon me-2 flex-shrink-0"></i>
          <div class="search-error-text">{{ error_message }}</div>
        </div>
      </div>

      <!-- Results -->
      <div v-else-if="results.length">
        <ul class="list-group list-group-flush">
          <li
            v-for="(item, index) in results"
            :key="`${item.source}-${item.source_id}`"
            class="list-group-item result-item border-0 border-bottom"
            :class="{ 'border-bottom-0': index === results.length - 1 }"
            @click="on_select(item)"
            @keyup.enter="on_select(item)"
            tabindex="0"
          >
            <div class="d-flex align-items-start">
              <!-- Icon based on source type -->
              <div class="result-icon me-3 mt-1 flex-shrink-0">
                <i
                  class="text-primary"
                  :class="{
                    'bi bi-diagram-3': item.source === 'entity_type',
                    'bi bi-building': item.source === 'entity_profile',
                    'bi bi-file-earmark-text': item.source === 'application',
                    'bi bi-person': item.source === 'user',
                    'bi bi-shield-check': item.source === 'role',
                    'bi bi-file-text': !['entity_type', 'entity_profile', 'application', 'user', 'role'].includes(item.source)
                  }"
                ></i>
              </div>

              <!-- Content -->
              <div class="result-title fw-semibold mb-1">
                <template v-if="Array.isArray(highlight(item.title))">
                  <template v-for="(part, i) in highlight(item.title)" :key="i">
                    <mark v-if="part.highlight">{{ part.text }}</mark>
                    <template v-else>{{ part.text }}</template>
                  </template>
                </template>
                <template v-else>{{ highlight(item.title) }}</template>
              </div>

              <div v-if="item.extra && item.extra.is_subentity" class="result-summary text-muted">
                <em>Sub-entity of {{ item.extra.parent_entity_name || ('Entity ' + item.extra.parent_entity_id) }}</em>
                <span v-if="item.extra.parent_status"> · Status: {{ item.extra.parent_status }}</span>
              </div>
              <div v-else class="result-summary text-muted">
                <template v-if="Array.isArray(highlight(item.summary || ''))">
                  <template v-for="(part, i) in highlight(item.summary || '')" :key="i">
                    <mark v-if="part.highlight">{{ part.text }}</mark>
                    <template v-else>{{ part.text }}</template>
                  </template>
                </template>
                <template v-else>{{ highlight(item.summary || '') }}</template>
              </div>

              <!-- Arrow -->
              <div class="result-arrow ms-2 flex-shrink-0 text-muted">
                <i class="bi bi-chevron-right"></i>
              </div>
            </div>
          </li>
        </ul>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!is_searching && searchQuery"
        class="search-empty text-center py-5 px-4"
      >
        <i class="bi bi-search search-empty-icon text-muted mb-3"></i>
        <h6 class="search-empty-title text-muted mb-2">No results found</h6>
        <p class="search-empty-subtitle text-muted mb-0">
          Try adjusting your search terms
        </p>
      </div>
    </div>
  </div>
</div>
